import React, { FunctionComponent, useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useDebouncedCallback } from 'use-debounce';
import { GamePlayDataModel } from './GamePlayDataModel';
import './GamePlayHistoryList.scss';
import { Pagination, PaginationInterface } from '../../shared/components/pagination/Pagination';
import { SearchField } from '../../shared/components/search-field/SearchField';
import GamePlayHistory from './GamePlayHistory';
import { SEARCH_PRODUCTS } from './useGamePlay';

interface GamePlayHistoryListProps {
    games: GamePlayDataModel[];
}

export const GamePlayHistoryList: FunctionComponent<GamePlayHistoryListProps> = ({ games }) => {
    const [filteredGames, setFilteredGames] = useState<GamePlayDataModel[]>(games);
    const [pagination, setPagination] = useState<PaginationInterface>({
        totalPages: 1,
        currentPage: 1,
        pageSize: 5,
        numItems: 0,
    });

    const pageStartIndex = (pagination.currentPage - 1) * pagination.pageSize;
    const pageEndIndex = pageStartIndex + pagination.pageSize;
    const gamesOnPage = filteredGames.slice(pageStartIndex, pageEndIndex);

    useEffect(() => {
        setPagination((prev) => ({
            ...prev,
            totalPages: Math.ceil(filteredGames.length / prev.pageSize),
            currentPage: 1,
            numItems: filteredGames.length,
        }));
    }, [filteredGames, pagination.pageSize]);

    const [getNcomGames] = useLazyQuery(SEARCH_PRODUCTS, {
        onCompleted: (ncomSearchResults) => {
            if (ncomSearchResults.productSearch?.nodes.length > 0) {
                const searchResults = ncomSearchResults?.productSearch?.nodes.map(
                    (game: { applicationId: string }) => game.applicationId,
                );

                setFilteredGames(
                    games?.filter((game) => searchResults.includes(game.applicationId)),
                );
            } else {
                setFilteredGames([]);
            }
        },
    });

    const debouncedSearch = useDebouncedCallback((searchText) => {
        if (searchText) {
            getNcomGames({
                variables: {
                    searchText,
                },
            });
        } else {
            setFilteredGames(games);
        }
    }, 500);

    return (
        <div className="game-play-history-list p-3">
            <SearchField
                placeholder="Search game title"
                onQueryChange={(text) => debouncedSearch(text)}
            />
            <ol className="list-unstyled">
                {gamesOnPage.map((game) => (
                    <GamePlayHistory
                        key={game.applicationId}
                        applicationId={game.applicationId}
                        playTime={game.playTime}
                        consumerId={game.consumerId}
                    />
                ))}
            </ol>
            <Pagination pagination={pagination} updatePagination={setPagination} />
        </div>
    );
};
